import { useState } from 'react';
import { supabase } from '../lib/supabase';
import { useNavigate, useLocation } from 'react-router-dom';
import { CheckCircleIcon, ExclamationTriangleIcon, EnvelopeIcon } from '@heroicons/react/24/outline';

type AuthMode = 'login' | 'register-company' | 'register-employee' | 'reset-password';

export default function Auth() {
  const [loading, setLoading] = useState(false);
  const [authMode, setAuthMode] = useState<AuthMode>('login');
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [companyName, setCompanyName] = useState('');
  const [fullName, setFullName] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [successMessage, setSuccessMessage] = useState<string | null>(null);
  const navigate = useNavigate();
  const location = useLocation();

  const resetSuccessMessage = location.state?.message;

  const switchMode = (newMode: AuthMode) => {
    setAuthMode(newMode);
    setError(null);
    setSuccessMessage(null);
    setEmail('');
    setPassword('');
    setCompanyName('');
    setFullName('');
  };

  const handleCompanySignUp = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    setError(null);
    setSuccessMessage(null);
    setLoading(true);

    try {
      const { data: authData, error: authError } = await supabase.auth.signUp({
        email,
        password,
        options: {
          data: {
            company_name: companyName,
            full_name: fullName,
          },
        },
      });

      if (authError) throw authError;

      if (authData.user?.identities?.length === 0) {
        setError('Dit email adres is al geregistreerd. Probeer in te loggen.');
        return;
      }

      setSuccessMessage('Bedrijf succesvol aangemaakt! Check je inbox voor de verificatie email.');
      
      // 🚨 BUGFIX: Maak de state leeg na succes om re-submit te voorkomen
      setEmail('');
      setPassword('');
      setCompanyName('');
      setFullName('');
      
    } catch (error: any) {
      console.error('Registratie fout:', error);
      setError(error.message || 'Er is een onverwachte fout opgetreden.');
    } finally {
      setLoading(false);
    }
  };

  const handleEmployeeSignUp = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    setError(null);
    setSuccessMessage(null);
    setLoading(true);

    try {
      const { data: existingEmployee, error: checkError } = await supabase
        .from('employees')
        .select('id, user_id')
        .eq('email', email)
        .maybeSingle();

      if (checkError) throw checkError;
      if (!existingEmployee) throw new Error('Dit email adres is niet geregistreerd als medewerker. Neem contact op met je manager.');
      if (existingEmployee.user_id) throw new Error('Dit email adres heeft al een account. Probeer in te loggen.');

      const { data: authData, error: signupError } = await supabase.auth.signUp({
        email,
        password,
        options: {
          data: {
            full_name: fullName || email.split('@')[0],
          }
        }
      });

      if (signupError) throw signupError;

      if (authData.user?.identities?.length === 0) {
        setError('Dit email adres is al geregistreerd. Probeer in te loggen.');
        return;
      }

      setSuccessMessage('Account aangemaakt! Check je email voor verificatie. De trigger heeft je automatisch gelinkt aan je employee record.');
      
      // 🚨 BUGFIX: Maak de state leeg na succes om re-submit te voorkomen
      setEmail('');
      setPassword('');
      setFullName('');

    } catch (error: any) {
      console.error('Employee signup fout:', error);
      setError(error.message || 'Er is een onverwachte fout opgetreden.');
    } finally {
      setLoading(false);
    }
  };

  const handlePasswordReset = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    setError(null);
    setSuccessMessage(null);
    setLoading(true);

    if (!email || !email.trim()) {
      setError('Email adres is verplicht');
      setLoading(false);
      return;
    }

    try {
      const { error } = await supabase.auth.resetPasswordForEmail(email, {
        redirectTo: `${window.location.origin}/reset-password`,
      });

      if (error) throw error;

      setSuccessMessage(`Wachtwoord reset link verstuurd naar ${email}! Check je inbox (en spam folder).`);
      
      setTimeout(() => switchMode('login'), 5000);
      
    } catch (error: any) {
      console.error('Password reset error:', error);
      setError(error.message || 'Er is een fout opgetreden bij het versturen van de reset link.');
    } finally {
      setLoading(false);
    }
  };

  const handleLogin = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    setError(null);
    setLoading(true);

    try {
      const { error } = await supabase.auth.signInWithPassword({ email, password });
      if (error) throw error;
      
      navigate('/dashboard');
    } catch (error: any) {
      if (error.message?.includes('Invalid login credentials')) {
        setError('Ongeldige inloggegevens. Controleer je email en wachtwoord.');
      } else if (error.message?.includes('Email not confirmed')) {
        setError('Email nog niet geverifieerd. Check je inbox voor de verificatie link.');
      } else {
        setError(error.message || 'Er is een fout opgetreden bij het inloggen.');
      }
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    if (authMode === 'login') handleLogin(e);
    else if (authMode === 'register-company') handleCompanySignUp(e);
    else if (authMode === 'register-employee') handleEmployeeSignUp(e);
    else if (authMode === 'reset-password') handlePasswordReset(e);
  };

  return (
    <div className="min-h-screen bg-gray-50 flex flex-col justify-center py-12 sm:px-6 lg:px-8">
      <div className="sm:mx-auto sm:w-full sm:max-w-md">
        <h2 className="mt-6 text-center text-3xl font-extrabold text-gray-900">
          {authMode === 'login' && 'Log in op je account'}
          {authMode === 'register-company' && 'Registreer een nieuw bedrijf'}
          {authMode === 'register-employee' && 'Maak een medewerker account'}
          {authMode === 'reset-password' && 'Wachtwoord herstellen'}
        </h2>
      </div>

      <div className="mt-8 sm:mx-auto sm:w-full sm:max-w-md">
        <div className="bg-white py-8 px-4 shadow sm:rounded-lg sm:px-10">
          {resetSuccessMessage && authMode === 'login' && (
            <div className="mb-6 rounded-md bg-green-50 dark:bg-green-900/20 p-4 border border-green-200">
              <div className="flex">
                <div className="flex-shrink-0">
                  <CheckCircleIcon className="h-5 w-5 text-green-400" />
                </div>
                <div className="ml-3">
                  <p className="text-sm text-green-800 dark:text-green-200">{resetSuccessMessage}</p>
                </div>
              </div>
            </div>
          )}

          {/* 🚨 BUGFIX: Toon het formulier NIET als er een succesbericht is */}
          {!successMessage && (
            <form className="space-y-6" onSubmit={handleSubmit}>
              {error && (
                <div className="rounded-md bg-red-50 p-4">
                  <div className="flex">
                    <ExclamationTriangleIcon className="h-5 w-5 text-red-400" />
                    <p className="ml-3 text-sm font-medium text-red-800">{error}</p>
                  </div>
                </div>
              )}
              
              {authMode === 'register-company' && (
                <>
                  <div>
                    <label htmlFor="fullName" className="block text-sm font-medium text-gray-700">Volledige Naam</label>
                    <input id="fullName" type="text" value={fullName} onChange={(e) => setFullName(e.target.value)} placeholder="Jan Jansen" required className="mt-1 appearance-none block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm placeholder-gray-400 focus:outline-none focus:ring-indigo-500 focus:border-indigo-500 sm:text-sm" />
                  </div>
                  <div>
                    <label htmlFor="companyName" className="block text-sm font-medium text-gray-700">Bedrijfsnaam</label>
                    <input id="companyName" type="text" value={companyName} onChange={(e) => setCompanyName(e.target.value)} placeholder="Mijn Bedrijf BV" required className="mt-1 appearance-none block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm placeholder-gray-400 focus:outline-none focus:ring-indigo-500 focus:border-indigo-500 sm:text-sm" />
                  </div>
                </>
              )}

              {authMode === 'register-employee' && (
                <div>
                  <label htmlFor="fullName" className="block text-sm font-medium text-gray-700">Volledige Naam (optioneel)</label>
                  <input id="fullName" type="text" value={fullName} onChange={(e) => setFullName(e.target.value)} placeholder="Jan Jansen" className="mt-1 appearance-none block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm placeholder-gray-400 focus:outline-none focus:ring-indigo-500 focus:border-indigo-500 sm:text-sm" />
                </div>
              )}
              
              <div>
                <label htmlFor="email" className="block text-sm font-medium text-gray-700">E-mailadres</label>
                <input id="email" type="email" autoComplete="email" required value={email} onChange={(e) => setEmail(e.target.value)} placeholder={authMode === 'register-employee' ? 'je.werk@email.nl' : 'email@voorbeeld.nl'} className="mt-1 appearance-none block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm placeholder-gray-400 focus:outline-none focus:ring-indigo-500 focus:border-indigo-500 sm:text-sm" />
                {authMode === 'register-employee' && (
                  <p className="mt-1 text-xs text-gray-500">Gebruik je werk email waarmee je als medewerker bent toegevoegd</p>
                )}
              </div>
              
              {authMode !== 'reset-password' && (
                <div>
                  <label htmlFor="password" className="block text-sm font-medium text-gray-700">Wachtwoord</label>
                  <input id="password" type="password" autoComplete={authMode === 'login' ? "current-password" : "new-password"} required value={password} onChange={(e) => setPassword(e.target.value)} placeholder="Minimaal 6 tekens" className="mt-1 appearance-none block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm placeholder-gray-400 focus:outline-none focus:ring-indigo-500 focus:border-indigo-500 sm:text-sm" />
                </div>
              )}

              {authMode === 'login' && (
                <div className="flex items-center justify-end">
                  <button type="button" onClick={() => switchMode('reset-password')} className="text-sm font-medium text-indigo-600 hover:text-indigo-500">Wachtwoord vergeten?</button>
                </div>
              )}
              
              <button type="submit" disabled={loading} className="w-full flex justify-center py-2 px-4 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-indigo-600 hover:bg-indigo-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500 disabled:opacity-50 disabled:cursor-not-allowed">
                {loading ? (
                  <><div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></div>Bezig...</>
                ) : (
                  <>
                    {authMode === 'login' && 'Inloggen'}
                    {authMode === 'reset-password' && 'Verstuur reset link'}
                    {(authMode === 'register-company' || authMode === 'register-employee') && 'Registreren'}
                  </>
                )}
              </button>
            </form>
          )}
          
          {successMessage && (
            <div className="rounded-md bg-green-50 p-4 border border-green-200">
              <div className="flex items-start">
                <CheckCircleIcon className="h-5 w-5 text-green-400 mt-0.5 flex-shrink-0" />
                <div className="ml-3">
                  <p className="text-sm font-medium text-green-800">{successMessage}</p>
                  <div className="mt-3 bg-blue-50 border border-blue-200 rounded-md p-3">
                    <div className="flex items-start">
                      <EnvelopeIcon className="h-5 w-5 text-blue-500 mt-0.5 flex-shrink-0" />
                      <div className="ml-2">
                        <p className="text-sm font-semibold text-blue-900">Let op: Check ook je spam folder!</p>
                        <p className="text-xs text-blue-700 mt-1">De verificatie email kan in je spam/ongewenste mail terechtkomen.</p>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          )}
          
          <div className="mt-6 space-y-3">
            <div className="text-center">
              {authMode === 'login' && (
                <div className="space-y-2">
                  <button type="button" onClick={() => switchMode('register-employee')} className="text-sm font-medium text-indigo-600 hover:text-indigo-500">Werknemer? Maak een account</button>
                  <br />
                  <button type="button" onClick={() => switchMode('register-company')} className="text-sm font-medium text-indigo-600 hover:text-indigo-500">Nieuw bedrijf registreren</button>
                </div>
              )}
              
              {(authMode === 'register-company' || authMode === 'register-employee' || authMode === 'reset-password') && (
                <button type="button" onClick={() => switchMode('login')} className="text-sm font-medium text-indigo-600 hover:text-indigo-500">Al een account? Log in</button>
              )}
            </div>
          </div>

          {authMode === 'register-employee' && (
            <div className="mt-4 bg-blue-50 border border-blue-200 rounded-lg p-4">
              <p className="text-xs text-blue-800"><strong>Let op:</strong> Je kunt alleen registreren als je email adres al is toegevoegd door je manager.</p>
            </div>
          )}

          {authMode === 'reset-password' && (
            <div className="mt-4 bg-yellow-50 border border-yellow-200 rounded-lg p-4">
              <p className="text-xs text-yellow-800">Voer je email adres in en we sturen je een link om je wachtwoord opnieuw in te stellen.</p>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
